/**
 * PaymentsEarningsSection
 * Settings page section for Stripe Connect, income summary, and transaction history.
 * Matches Figma node 6447:28124.
 */

import { toast } from "sonner"
import {
  useConnectStatus,
  useCreateConnectAccount,
  useDashboardLink,
  useDisconnectAccount,
  useEarningsSummary,
  useRequestPayout,
} from "../hooks/useEarnings"
import { IncomeSummary } from "./IncomeSummary"
import { StripeConnectCard } from "./StripeConnectCard"
import { TransactionHistory } from "./TransactionHistory"

export function PaymentsEarningsSection() {
  const { data: connectStatus, isLoading: connectLoading } = useConnectStatus()
  const { data: summary, isLoading: summaryLoading } = useEarningsSummary()

  const createConnect = useCreateConnectAccount()
  const disconnect = useDisconnectAccount()
  const dashboardLink = useDashboardLink()
  const requestPayout = useRequestPayout()

  const handleConnect = async () => {
    try {
      const baseUrl = window.location.origin
      const result = await createConnect.mutateAsync({
        returnUrl: `${baseUrl}/settings?section=payments-earnings`,
        refreshUrl: `${baseUrl}/settings?section=payments-earnings`,
      })
      window.location.href = result.url
    } catch {
      toast.error("Failed to start Stripe onboarding")
    }
  }

  const handleManage = async () => {
    try {
      const result = await dashboardLink.mutateAsync()
      window.open(result.url, "_blank")
    } catch {
      toast.error("Failed to open Stripe dashboard")
    }
  }

  const handleDisconnect = async () => {
    if (!confirm("Are you sure you want to disconnect your Stripe account?")) return
    try {
      await disconnect.mutateAsync()
      toast.success("Stripe account disconnected")
    } catch {
      toast.error("Failed to disconnect account")
    }
  }

  const handlePayout = async () => {
    try {
      const result = await requestPayout.mutateAsync()
      toast.success(
        result.queueStatus === "already_in_progress"
          ? "A bank payout is already in progress in Stripe."
          : `Bank payout of ${formatCurrency(result.netAmount)} requested`,
      )
    } catch {
      toast.error("Failed to request payout. Please try again.")
    }
  }

  return (
    <div className="space-y-7.5">
      <SectionHeader />
      <StripeConnectCard
        connectStatus={connectStatus}
        isLoading={connectLoading}
        isConnecting={createConnect.isPending}
        isDisconnecting={disconnect.isPending}
        isManaging={dashboardLink.isPending}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        onManage={handleManage}
      />

      {connectStatus?.connected && (
        <>
          <Divider />
          <div className="space-y-7.5">
            <IncomeSummary
              summary={summary}
              isLoading={summaryLoading}
              isRequestingPayout={requestPayout.isPending}
              onPayout={handlePayout}
            />
            <Divider />
            <TransactionHistory />
          </div>
        </>
      )}
    </div>
  )
}

function SectionHeader() {
  return (
    <div className="flex flex-col gap-1.25">
      <h2 className="text-xl leading-5 font-semibold text-primary uppercase">
        Payments & Earnings
      </h2>
      <p className="text-sm leading-6.25 text-primary">View and update your payment details</p>
    </div>
  )
}

function Divider() {
  return <div className="border-t border-[#F2F0EB26]" />
}

function formatCurrency(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount)
}
